import { LegalPage, LegalSection, LegalSubsection } from "@/components/layout/LegalPage";
import { SEO } from "@/lib/seo";

const itemClass = "ml-5 list-disc space-y-2";

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy · PulseBoard Canberra"
        description="How PulseBoard Canberra collects, uses, protects, and stores practitioner and visitor information in Australia."
        path="/privacy"
      />
      <LegalPage
        eyebrow="Legal"
        title="Privacy Policy"
        intro="This policy explains how PulseBoard handles information about directory practitioners, registered users, practice staff, and visitors."
      >
        <LegalSection title="1. Who We Are">
          <p>
            <strong>PulseBoard</strong> is a product of <strong>Amida Health Pty Ltd</strong>{" "}
            ("Amida"), an Australian clinician-led health technology company and the maker
            of ClinicOS and CanvasMD. Learn more at{" "}
            <a className="pb-link" href="https://amidahealthai.com.au" target="_blank" rel="noreferrer">
              amidahealthai.com.au
            </a>
            .
          </p>
          <p>
            <strong>Contact for privacy matters:</strong>{" "}
            <a className="pb-link" href="mailto:hello@pulseboard.com.au">
              hello@pulseboard.com.au
            </a>
          </p>
          <p>
            In this policy, <strong>"we", "us", "our"</strong> means Amida Health Pty Ltd
            operating PulseBoard; <strong>"you"</strong> means anyone using PulseBoard,
            including general practitioners, medical specialists, allied health professionals,
            practice staff, and visitors; <strong>"Service"</strong> means the PulseBoard
            websites and associated APIs.
          </p>
        </LegalSection>

        <LegalSection title="2. Who This Service Is For">
          <p>
            PulseBoard is designed for <strong>registered Australian healthcare practitioners</strong>{" "}
            and their practice staff. It is not a patient-facing service. We do not collect,
            hold, or process patient health records.
          </p>
        </LegalSection>

        <LegalSection title="3. The PulseBoard Directory — Information About Practitioners">
          <p>
            PulseBoard publishes a directory of healthcare practitioners and practices in the
            Canberra region. This section explains how that information is handled, whether or
            not you have ever used PulseBoard.
          </p>
          <LegalSubsection title="3.1 What we publish">
            <p>
              Directory listings may include: practitioner name, specialty and focus areas,
              practice name and address, publicly listed contact details, referral guidance,
              telehealth and billing indicators, and website links.
            </p>
          </LegalSubsection>
          <LegalSubsection title="3.2 Where it comes from">
            <p>
              Listings are compiled from <strong>publicly available sources</strong>, including
              practice websites, publicly listed contact directories, the AHPRA public register,
              and professional directories. Every listing displays its source and the date it was
              last checked ("source-attributed"). We do not publish information obtained from
              private or patient-facing records.
            </p>
          </LegalSubsection>
          <LegalSubsection title="3.3 Your rights over your listing">
            <p>If you are a practitioner listed on PulseBoard, you may at any time:</p>
            <ul className={itemClass}>
              <li>
                <strong>Correct</strong> your listing — use the "Report inaccuracy" control on
                your profile or email hello@pulseboard.com.au.
              </li>
              <li>
                <strong>Claim</strong> your listing — verified practitioners can take control of
                their profile content.
              </li>
              <li>
                <strong>Request removal</strong> — we will suppress your listing from public
                display within <strong>10 business days</strong> of a verified request, unless we
                are required to retain it by law.
              </li>
            </ul>
            <p>
              We consider the publication of practitioners&apos; professional (not sensitive
              personal) details, drawn from public sources with attribution and a correction
              pathway, to be reasonable for the purpose of supporting clinical referral
              decisions. If you disagree, contact us and we will review promptly.
            </p>
          </LegalSubsection>
        </LegalSection>

        <LegalSection title="4. What Information We Collect From Users">
          <LegalSubsection title="4.1 Access requests">
            <p>
              When you submit an access request via the Service we collect: your email address,
              and optionally your name, practice/organisation, and message. We use this solely to
              assess and respond to your request and to administer the pilot program.
            </p>
          </LegalSubsection>
          <LegalSubsection title="4.2 Accounts (where enabled)">
            <p>
              If you create an account: name, email, phone, professional details (AHPRA
              registration number, provider number, specialty), practice details, authentication
              credentials (passwords stored hashed), and notification preferences, including
              consent records for email/SMS.
            </p>
          </LegalSubsection>
          <LegalSubsection title="4.3 Information collected automatically">
            <p>
              Log data (IP address, user agent, timestamps, request metadata) and, for signed-in
              users, an audit record of actions taken in the app. We use first-party, aggregated
              analytics only — no advertising trackers.
            </p>
          </LegalSubsection>
          <LegalSubsection title="4.4 Availability information (where published)">
            <p>
              Specialists and their practices may publish availability signals (for example,
              indicative wait times or open slots). This is practice-level information supplied
              voluntarily; it contains no patient information.
            </p>
          </LegalSubsection>
        </LegalSection>

        <LegalSection title="5. What We Do NOT Collect">
          <ul className={itemClass}>
            <li>Patient health records or patient-identifying information</li>
            <li>Payment card data (invoicing is handled separately with our accounting provider)</li>
            <li>Advertising identifiers or third-party tracking cookies</li>
          </ul>
        </LegalSection>

        <LegalSection title="6. How We Use Information">
          <p>
            To operate the Service (directory search, alerts, access management, availability
            display); to verify professional registration against the AHPRA public register; to
            respond to enquiries and administer the pilot; to improve the Service using aggregated,
            de-identified analytics; to meet legal obligations; and to detect misuse.
          </p>
          <p>
            <strong>We do not</strong> sell your data, share it with advertisers, or use your
            content to train AI models.
          </p>
        </LegalSection>

        <LegalSection title="7. Public Health Alerts">
          <p>
            Alert content shown on PulseBoard is ingested from official public sources (for
            example, ACT Health and ACT ESA) and displayed with source attribution. Alert content
            is public information and contains no personal information about you. Clinicians
            should verify alerts with the originating source before clinical action.
          </p>
        </LegalSection>

        <LegalSection title="8. How We Store and Protect Information">
          <ul className={itemClass}>
            <li>
              <strong>Data residency:</strong> all Service data is stored in <strong>Google Cloud
              Platform, australia-southeast1 (Sydney)</strong>. Service data is not transferred
              outside Australia.
            </li>
            <li><strong>Encryption:</strong> TLS in transit on all public endpoints; encryption at rest (AES-256) on databases and storage.</li>
            <li><strong>Access control:</strong> role-based access; administrative actions logged.</li>
            <li><strong>Retention:</strong> access-request data — 24 months, then deleted; account data — while active plus 7 years after closure; logs — 12 months; expired availability signals — 90 days; encrypted backups — 30 days rolling.</li>
            <li><strong>Deletion:</strong> request account/data deletion any time via hello@pulseboard.com.au; purged within 30 days, except records we must retain by law (retained de-identified where possible).</li>
          </ul>
        </LegalSection>

        <LegalSection title="9. Sharing of Information">
          <p>
            We share personal information only: (1) at your direction; (2) where required by law
            (we will notify you unless prohibited); (3) with the service providers below; (4) to
            prevent imminent harm; or (5) in a business transfer, with the same privacy
            obligations flowing through.
          </p>
          <LegalSubsection title="Service providers">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-pb-border text-pb-ink">
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2 pr-4">Purpose</th>
                    <th className="py-2">Region</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pb-border/60">
                  <tr><td className="py-2 pr-4">Google Cloud Platform</td><td className="py-2 pr-4">Hosting, database, storage</td><td className="py-2">australia-southeast1 (Sydney)</td></tr>
                  <tr><td className="py-2 pr-4">Transactional email provider</td><td className="py-2 pr-4">Service and pilot emails</td><td className="py-2">Australia-hosted or AU terms</td></tr>
                  <tr><td className="py-2 pr-4">Error monitoring</td><td className="py-2 pr-4">Redacted diagnostics</td><td className="py-2">Australia region</td></tr>
                  <tr><td className="py-2 pr-4">CDN</td><td className="py-2 pr-4">Static asset delivery</td><td className="py-2">Global edge (assets only)</td></tr>
                </tbody>
              </table>
            </div>
          </LegalSubsection>
        </LegalSection>

        <LegalSection title="10. (Planned) Google User Data — Calendar Integration">
          <p>
            If and when you connect a Google Calendar to publish availability, we will request
            read-only access to the single calendar you select, use it solely to derive
            availability windows, never read attendee details, never modify your calendar, never
            use the data for advertising or AI training, and purge derived data within 24 hours
            of disconnect — in accordance with Google&apos;s API Services User Data Policy
            (Limited Use). Full details will be presented at the point of connection. You can
            revoke access at any time at{" "}
            <a className="pb-link" href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
              myaccount.google.com/permissions
            </a>
            .
          </p>
        </LegalSection>

        <LegalSection title="11. Cookies">
          <p>We use strictly necessary cookies only (session management, security). No advertising or third-party analytics cookies.</p>
        </LegalSection>

        <LegalSection title="12. Your Rights Under the Privacy Act">
          <p>
            You may request <strong>access</strong> to your personal information, <strong>correction</strong>{" "}
            of inaccurate information, or <strong>deletion</strong> of your data via
            hello@pulseboard.com.au. We respond within 30 calendar days. If you believe we have
            mishandled your information and we cannot resolve it, you may complain to the{" "}
            <a className="pb-link" href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">
              Office of the Australian Information Commissioner (OAIC)
            </a>
            .
          </p>
        </LegalSection>

        <LegalSection title="13. Notifiable Data Breaches">
          <p>If a data breach occurs that is likely to result in serious harm, we will notify affected individuals and the OAIC in accordance with the Notifiable Data Breaches scheme.</p>
        </LegalSection>

        <LegalSection title="14. Children">
          <p>PulseBoard is not intended for anyone under 18. It is a professional service for registered healthcare practitioners and their staff.</p>
        </LegalSection>

        <LegalSection title="15. Changes to This Policy">
          <p>We may update this policy. Material changes will be notified on the Service (and by email to account holders) at least 14 days before taking effect.</p>
        </LegalSection>

        <LegalSection title="16. Contact">
          <p>
            <strong>Privacy:</strong> <a className="pb-link" href="mailto:hello@pulseboard.com.au">hello@pulseboard.com.au</a><br />
            <strong>Support:</strong> <a className="pb-link" href="mailto:support@pulseboard.com.au">support@pulseboard.com.au</a><br />
            <strong>General:</strong> <a className="pb-link" href="mailto:info@amidahealthai.com.au">info@amidahealthai.com.au</a>
          </p>
          <p><em>PulseBoard is operated in Australia by Amida Health Pty Ltd. Your data stays in Australia.</em></p>
        </LegalSection>
      </LegalPage>
    </>
  );
}
